/**
 * themes.js — White-label theme system for HAVN.
 *
 * Usage:
 *   import { ThemeProvider, useTheme, THEMES } from '@/themes/themes';
 *
 *   // Wrap app:
 *   <ThemeProvider><App /></ThemeProvider>
 *
 *   // In any component:
 *   const { theme, themeName, setTheme, themeNames } = useTheme();
 *   theme.primary  → '#3FAFD1'
 *
 * Adding a new bank theme:
 *   1. Add an entry to THEMES below
 *   2. That's it — it appears in the admin selector automatically
 *
 * All colors are applied as CSS variables on :root so they cascade everywhere.
 */

import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

// ── Theme definitions ─────────────────────────────────────────────
export const THEMES = {
  havn: {
    label: 'HAVN',
    // Landing-page design system: signal red-orange accent on deep ink blue,
    // bond-paper off-white text. Matches havnassist.com.
    primary: '#ff4326', primaryDark: '#d8331a', primaryLight: '#ff6b4a',
    secondary: '#f4f1e8', accent: '#ff4326',
    background: '#0d1b2a', backgroundLight: '#12243a', surface: '#12243a',
    surfaceRgba: 'rgba(18,36,58,0.92)', border: '#1b3350', borderSubtle: 'rgba(27,51,80,0.35)',
    text: '#f4f1e8', textSecondary: '#b9c2cf', textMuted: '#7d8aa0',
    safe: '#22c55e', danger: '#ef4444', caution: '#f59e0b',
    logoTint: 'none',
    orbImage: '/assets/orb_havn_v4.png',
    backgroundImage: '/assets/havn_bg_dark.png',
    glassTint: 'rgba(64, 68, 76, 0.62)',
    glass: 'rgba(27, 51, 80, 0.40)',
    haloColor: '#ff8a66',
    blobOpacity: 0.5, haloOpacity: 0.45,
  },
  cibc: {
    label: 'CIBC',
    primary: '#C41F3E', primaryDark: '#9E1932', primaryLight: '#D94A63',
    secondary: '#FFFFFF', accent: '#C41F3E',
    background: '#1A0608', backgroundLight: '#2A0A0E', surface: '#2A0A0E',
    surfaceRgba: 'rgba(42,10,14,0.92)', border: '#4A1A1E', borderSubtle: 'rgba(74,26,30,0.3)',
    text: '#F0F0F0', textSecondary: '#C0A0A4', textMuted: '#907074',
    safe: '#22c55e', danger: '#ef4444', caution: '#f59e0b',
    logoTint: 'brightness(0) saturate(100%) invert(22%) sepia(85%) saturate(3500%) hue-rotate(340deg)',
    orbImage: '/assets/orb_cibc_v4.png',
    backgroundImage: '/assets/background_cibc.png',
    glassTint: 'rgba(64, 68, 76, 0.62)',
    glass: 'rgba(65, 42, 52, 0.40)',
    haloColor: '#FF6B7A',
    blobOpacity: 0.28, haloOpacity: 0.45,
  },
  td: {
    label: 'TD',
    primary: '#2B8000', primaryDark: '#1F5E00', primaryLight: '#3DA310',
    secondary: '#FFFFFF', accent: '#2B8000',
    background: '#051A0E', backgroundLight: '#0A2818', surface: '#0A2818',
    surfaceRgba: 'rgba(10,40,24,0.92)', border: '#1A3828', borderSubtle: 'rgba(26,56,40,0.3)',
    text: '#F0F0F0', textSecondary: '#A0C0B0', textMuted: '#708070',
    safe: '#22c55e', danger: '#ef4444', caution: '#f59e0b',
    logoTint: 'brightness(0) saturate(100%) invert(38%) sepia(95%) saturate(1200%) hue-rotate(85deg)',
    orbImage: '/assets/orb_td.png',
    backgroundImage: '/assets/Smooth_blue_gradient_backdrop.png',
    glassTint: 'rgba(64, 68, 76, 0.62)',
    glass: 'rgba(42, 60, 48, 0.40)',
    haloColor: '#5FD17A',
    blobOpacity: 0.28, haloOpacity: 0.45,
  },
  desjardins: {
    label: 'Desjardins',
    primary: '#00874E', primaryDark: '#006B3E', primaryLight: '#00A85E',
    secondary: '#FFFFFF', accent: '#00874E',
    background: '#04150C', backgroundLight: '#082014', surface: '#082014',
    surfaceRgba: 'rgba(8,32,20,0.92)', border: '#183828', borderSubtle: 'rgba(24,56,40,0.3)',
    text: '#F0F0F0', textSecondary: '#A0B8A8', textMuted: '#688070',
    safe: '#22c55e', danger: '#ef4444', caution: '#f59e0b',
    logoTint: 'brightness(0) saturate(100%) invert(35%) sepia(90%) saturate(1500%) hue-rotate(130deg)',
    orbImage: '/assets/orb_desjardins.png',
    backgroundImage: '/assets/Smooth_blue_gradient_backdrop.png',
    glassTint: 'rgba(64, 68, 76, 0.62)',
    glass: 'rgba(38, 58, 48, 0.40)',
    haloColor: '#4FC98A',
    blobOpacity: 0.28, haloOpacity: 0.45,
  },
  rbc: {
    label: 'RBC',
    primary: '#0051A5', primaryDark: '#003D7A', primaryLight: '#1A6FBF',
    secondary: '#FFD100', accent: '#FFD100',
    background: '#002B5C', backgroundLight: '#0A3568', surface: '#0A3568',
    surfaceRgba: 'rgba(10,53,104,0.92)', border: '#1A4578', borderSubtle: 'rgba(26,69,120,0.3)',
    text: '#F0F0F0', textSecondary: '#A0B8D0', textMuted: '#7090B0',
    safe: '#22c55e', danger: '#ef4444', caution: '#f59e0b',
    logoTint: 'brightness(0) saturate(100%) invert(22%) sepia(90%) saturate(2500%) hue-rotate(200deg)',
    orbImage: '/assets/orb_rbc.png',
    backgroundImage: '/assets/Smooth_blue_gradient_backdrop.png',
    glassTint: 'rgba(64, 68, 76, 0.62)',
    glass: 'rgba(42, 55, 82, 0.40)',
    haloColor: '#5A9FE8',
    blobOpacity: 0.28, haloOpacity: 0.45,
  },
  national_bank: {
    label: 'National Bank',
    primary: '#E31937', primaryDark: '#B81530', primaryLight: '#F03050',
    secondary: '#FFFFFF', accent: '#E31937',
    background: '#1C0608', backgroundLight: '#2C0A0E', surface: '#2C0A0E',
    surfaceRgba: 'rgba(44,10,14,0.92)', border: '#4C1A1E', borderSubtle: 'rgba(76,26,30,0.3)',
    text: '#F0F0F0', textSecondary: '#C0A0A4', textMuted: '#907074',
    safe: '#22c55e', danger: '#ef4444', caution: '#f59e0b',
    logoTint: 'brightness(0) saturate(100%) invert(18%) sepia(95%) saturate(4000%) hue-rotate(345deg)',
    orbImage: '/assets/orb_national_bank.png',
    backgroundImage: '/assets/Smooth_blue_gradient_backdrop.png',
    glassTint: 'rgba(64, 68, 76, 0.62)',
    glass: 'rgba(66, 42, 48, 0.40)',
    haloColor: '#FF5F4A',
    blobOpacity: 0.28, haloOpacity: 0.45,
  },
  bmo: {
    label: 'BMO',
    primary: '#0079C1', primaryDark: '#005A92', primaryLight: '#1A90D5',
    secondary: '#ED1C24', accent: '#0079C1',
    background: '#001528', backgroundLight: '#002240', surface: '#002240',
    surfaceRgba: 'rgba(0,34,64,0.92)', border: '#1A3858', borderSubtle: 'rgba(26,56,88,0.3)',
    text: '#F0F0F0', textSecondary: '#A0A8B5', textMuted: '#808890',
    safe: '#22c55e', danger: '#ef4444', caution: '#f59e0b',
    logoTint: 'brightness(0) saturate(100%) invert(30%) sepia(85%) saturate(2000%) hue-rotate(190deg)',
    orbImage: '/assets/orb_bmo.png',
    backgroundImage: '/assets/Smooth_blue_gradient_backdrop.png',
    glassTint: 'rgba(64, 68, 76, 0.62)',
    glass: 'rgba(40, 52, 72, 0.40)',
    haloColor: '#4FA8E8',
    blobOpacity: 0.28, haloOpacity: 0.45,
  },
  scotiabank: {
    label: 'Scotiabank',
    primary: '#EC111A', primaryDark: '#C00E15', primaryLight: '#F53040',
    secondary: '#FFFFFF', accent: '#EC111A',
    background: '#1A0505', backgroundLight: '#2A0808', surface: '#2A0808',
    surfaceRgba: 'rgba(42,8,8,0.92)', border: '#4A1818', borderSubtle: 'rgba(74,24,24,0.3)',
    text: '#F0F0F0', textSecondary: '#C0A0A0', textMuted: '#906868',
    safe: '#22c55e', danger: '#ef4444', caution: '#f59e0b',
    logoTint: 'brightness(0) saturate(100%) invert(15%) sepia(95%) saturate(5000%) hue-rotate(350deg)',
    orbImage: '/assets/orb_scotiabank.png',
    backgroundImage: '/assets/Smooth_blue_gradient_backdrop.png',
    glassTint: 'rgba(64, 68, 76, 0.62)',
    glass: 'rgba(68, 40, 42, 0.40)',
    haloColor: '#FF5550',
    blobOpacity: 0.28, haloOpacity: 0.45,
  },
};

export const THEME_NAMES = Object.keys(THEMES);
const STORAGE_KEY = 'havn_theme';
const DEFAULT_THEME = process.env.REACT_APP_THEME || 'havn';

// ── Hex to RGB helper ─────────────────────────────────────────────
function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r}, ${g}, ${b}`;
}

// ── Apply CSS variables ───────────────────────────────────────────
function applyThemeVars(theme) {
  const root = document.documentElement;
  root.style.setProperty('--havn-primary', theme.primary);
  root.style.setProperty('--havn-primary-dark', theme.primaryDark);
  root.style.setProperty('--havn-primary-light', theme.primaryLight);
  root.style.setProperty('--havn-secondary', theme.secondary);
  root.style.setProperty('--havn-accent', theme.accent);
  root.style.setProperty('--havn-bg', theme.background);
  root.style.setProperty('--havn-bg-light', theme.backgroundLight);
  root.style.setProperty('--havn-surface', theme.surface);
  root.style.setProperty('--havn-surface-rgba', theme.surfaceRgba);
  root.style.setProperty('--havn-border', theme.border);
  root.style.setProperty('--havn-border-subtle', theme.borderSubtle);
  root.style.setProperty('--havn-text', theme.text);
  root.style.setProperty('--havn-text-secondary', theme.textSecondary);
  root.style.setProperty('--havn-text-muted', theme.textMuted);
  root.style.setProperty('--havn-safe', theme.safe);
  root.style.setProperty('--havn-danger', theme.danger);
  root.style.setProperty('--havn-caution', theme.caution);
  root.style.setProperty('--havn-logo-tint', theme.logoTint);
  root.style.setProperty('--havn-primary-10', theme.primary + '1a');
  root.style.setProperty('--havn-primary-20', theme.primary + '33');
  // RGB version for rgba() usage in CSS (taskbar, utility buttons, orb glow)
  root.style.setProperty('--sk-primary-rgb', hexToRgb(theme.primary));
  root.style.setProperty('--havn-bg-rgb', hexToRgb(theme.background));
  root.style.setProperty('--havn-border-rgb', hexToRgb(theme.border));
  if (theme.glassTint) root.style.setProperty('--havn-glass-tint', theme.glassTint);
  if (theme.glass) root.style.setProperty('--havn-glass', theme.glass);
  root.style.setProperty('--havn-halo-color', theme.haloColor);
  root.style.setProperty('--havn-blob-opacity', theme.blobOpacity ?? 0.55);
  root.style.setProperty('--havn-halo-opacity', theme.haloOpacity ?? 0.45);
}

// ── Context ───────────────────────────────────────────────────────
const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [themeName, setThemeName] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && THEMES[saved]) return saved;
    } catch {}
    return DEFAULT_THEME;
  });

  const theme = THEMES[themeName] || THEMES.havn;

  // Apply theme vars synchronously on first render to minimise FOUC
  const [themeReady, setThemeReady] = useState(() => {
    applyThemeVars(theme);
    return true;
  });

  const setTheme = useCallback((name) => {
    if (!THEMES[name]) return;
    setThemeName(name);
    try { localStorage.setItem(STORAGE_KEY, name); } catch {}
  }, []);

  // Apply CSS variables whenever theme changes
  useEffect(() => {
    applyThemeVars(theme);
    if (!themeReady) setThemeReady(true);
  }, [theme, themeReady]);

  const value = useMemo(() => ({
    theme,
    themeName,
    setTheme,
    themeReady,
    themeNames: THEME_NAMES,
    themes: THEMES,
  }), [theme, themeName, setTheme, themeReady]);

  return (
    <ThemeContext.Provider value={value}>
      <div style={{
        opacity: themeReady ? 1 : 0,
        transition: 'opacity 200ms ease-in',
      }}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
